import {
    Description,
    Field,
    Fieldset,
    Input,
    Label,
    Legend,
    Select,
    Textarea,
} from '@headlessui/react'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { FaChevronDown, FaEye, FaEyeSlash, FaGlobe, FaUpload } from 'react-icons/fa'
import { useQuill } from 'react-quilljs'
import 'quill/dist/quill.snow.css'

export function FormUpload({
    label,
    description,
    className = '',
    error = '',
    required = false,
    accept = 'image/png, image/jpeg, image/jpg, application/pdf',
    onChange = () => {},
    initialUrl = '',
    initialFileName = '',
    readOnly = false,
    ...props
}) {
    const [preview, setPreview] = useState('')
    const [fileName, setFileName] = useState('')
    const [fileType, setFileType] = useState('')

    useEffect(() => {
        setFileName(initialFileName)

        if (initialUrl instanceof File) {
            setFileName(initialUrl.name)
            setFileType(initialUrl.type)

            if (initialUrl.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onloadend = () => {
                    setPreview(reader.result)
                }
                reader.readAsDataURL(initialUrl)
            } else {
                setPreview(null)
            }
        } else if (typeof initialUrl === 'string' && initialUrl !== '') {
            // assume it's a URL
            if (initialUrl.endsWith('.pdf')) {
                setFileType('application/pdf')
                setPreview(null)
            } else {
                setFileType('image/*')
                setPreview(initialUrl)
            }
        }
    }, [initialUrl, initialFileName])

    const handleFileChange = e => {
        const file = e.target.files?.[0]
        if (!file) return

        setFileName(file.name)
        setFileType(file.type)
        onChange(file)

        if (file.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setPreview(reader.result)
            }
            reader.readAsDataURL(file)
        } else {
            setPreview(null)
        }
    }

    return (
        <Field className='space-y-1'>
            <div>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                {description && (
                    <Description className='text-sm text-gray-500'>{description}</Description>
                )}
            </div>

            <div className='relative'>
                {!readOnly && (
                    <>
                        <label
                            htmlFor={props.id || props.name}
                            className={clsx(
                                'flex items-center justify-center w-full px-4 py-2 text-sm border-2 border-dashed rounded-lg cursor-pointer',
                                'hover:bg-gray-50 text-gray-600 border-gray-300',
                                'focus:outline-none focus:ring-2 focus:ring-yellow-200',
                                className
                            )}
                        >
                            <FaUpload className='mr-2' />
                            {fileName || initialUrl ? 'Change File' : 'Upload File'}
                        </label>
                        <input
                            id={props.id || props.name}
                            type='file'
                            accept={accept}
                            onChange={handleFileChange}
                            className='hidden'
                            {...props}
                        />
                        {fileName && (
                            <div className='mt-2 text-sm text-gray-600'>
                                <span className='font-medium'>Selected:</span> {fileName}
                            </div>
                        )}
                    </>
                )}
                {preview && (
                    <div className='mt-2'>
                        <img
                            src={preview}
                            alt='Preview not available'
                            className='border border-gray-300 rounded max-h-40'
                        />
                    </div>
                )}

                {!preview && fileType === 'application/pdf' && (
                    <div className='mt-2 text-sm italic text-gray-600'>
                        PDF selected. No preview available.
                    </div>
                )}
            </div>

            {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
        </Field>
    )
}

// Reusable Input Component
export function FormInput({
    label,
    description,
    className = '',
    error = '',
    required = false,
    type = '',
    readOnly = false,
    ...props
}) {
    const [showPassword, setShowPassword] = useState(false)

    const isPassword = type === 'password'

    const inputType =
        {
            datetime: 'datetime-local',
            date: 'date',
            time: 'time',
            email: 'email',
            number: 'number',
            password: showPassword ? 'text' : 'password',
            '': 'text',
        }[type] || 'text'

    const handleKeyDown = e => {
        if (type === 'number') {
            const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete']
            const isDigit = /^[0-9]$/.test(e.key)
            if (!isDigit && !allowedKeys.includes(e.key)) {
                e.preventDefault()
            }
        }
    }

    return (
        <Field className='space-y-1'>
            <div>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                {description && (
                    <Description className='text-sm text-gray-500'>{description}</Description>
                )}
            </div>

            <div className='relative'>
                {readOnly ? (
                    <span
                        className={clsx(
                            'block w-full bg-transparent py-2 border border-transparent',
                            className
                        )}
                    >
                        {props.value || '-'}
                    </span>
                ) : (
                    <>
                        <Input
                            type={inputType}
                            className={clsx(
                                'w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm shadow-sm',
                                'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                                'disabled:bg-gray-100 disabled:text-gray-500',
                                className
                            )}
                            onKeyDown={handleKeyDown}
                            {...props}
                        />
                        {isPassword && (
                            <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute text-gray-500 -translate-y-1/2 right-3 top-1/2 hover:text-gray-700'
                                tabIndex={-1}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        )}
                    </>
                )}
            </div>

            {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
        </Field>
    )
}

export function FormInputLang({
    label,
    description,
    className = '',
    error = {},
    required = false,
    readOnly = false,
    name,
    values,
    onChange,
    isTextarea = false, // <-- optional prop
    isRTF = false,
    groupIndex = null,
    ...props
}) {
    const [currentLang, setCurrentLang] = useState('en')

    const langSuffix = currentLang === 'ar' ? '_ar' : ''
    const fullName = name + langSuffix
    const value =
        groupIndex !== null
            ? values[name + langSuffix] || '' // For groups, values is the group object
            : values[fullName] || '' // For non-groups, values is allValues

    // const value = values[fullName] || ''
    const handleChange = e => {
        if (groupIndex) {
            // If in a group, we need to handle differently
            onChange(name, e.target.value, langSuffix)
        } else {
            // Regular non-group behavior
            onChange(fullName, e.target.value)
        }
    }

    const handleRichTextChange = val => {
        if (groupIndex !== null) {
            onChange(name, val, langSuffix)
        } else {
            onChange(fullName, val)
        }
    }

    return (
        <Field className='space-y-1'>
            <div className='flex items-center justify-between'>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                <button
                    type='button'
                    className='flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700'
                    onClick={() => setCurrentLang(currentLang === 'en' ? 'ar' : 'en')}
                >
                    <FaGlobe className='inline-block' />
                    {currentLang === 'en' ? 'EN' : 'AR'}
                </button>
            </div>

            {description && (
                <Description className='text-sm text-gray-500'>{description}</Description>
            )}

            {readOnly && !isRTF ? (
                <span
                    className={clsx(
                        'block w-full bg-transparent py-2 border border-transparent',
                        className
                    )}
                >
                    {value || '-'}
                </span>
            ) : (
                <>
                    {isTextarea ? (
                        <Textarea
                            className={clsx(
                                'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm',
                                'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                                'disabled:bg-gray-100 disabled:text-gray-500',
                                className
                            )}
                            value={value}
                            onChange={handleChange}
                            {...props}
                        />
                    ) : isRTF ? (
                        <FormRichText
                            key={fullName}
                            value={value}
                            onChange={handleRichTextChange}
                            readOnly={readOnly}
                            className={className}
                        />
                    ) : (
                        <Input
                            className={clsx(
                                'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm',
                                'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                                'disabled:bg-gray-100 disabled:text-gray-500',
                                className
                            )}
                            value={value}
                            onChange={handleChange}
                            {...props}
                        />
                    )}
                    {(error?.en || error?.ar) && (
                        <div className='mt-1 space-y-1 text-sm text-red-500'>
                            {currentLang === 'en' && error?.en && <p>{error.en}</p>}
                            {currentLang === 'ar' && error?.ar && <p>{error.ar}</p>}
                            {currentLang === 'ar' && error?.en && (
                                <p className='opacity-80'>{error.en}</p>
                            )}
                            {currentLang === 'en' && error?.ar && (
                                <p className='opacity-80'>{error.ar}</p>
                            )}
                        </div>
                    )}
                </>
            )}
        </Field>
    )
}

// Reusable Select Component
export function FormSelect({
    label,
    description,
    options,
    className = '',
    error = '',
    required = false,
    defaultValue,
    readOnly = false,
    ...props
}) {
    // Safely find the selected option label
    const getSelectedLabel = () => {
        const value = props.value

        // Handle null/undefined/empty values
        if (value === null || value === undefined || value === '') {
            return '-'
        }

        // Handle array values (for multi-select)
        if (Array.isArray(value)) {
            if (value.length === 0) return '-'

            return value
                .map(val => {
                    const option = options?.find(
                        opt => String(opt.value) === String(val) || opt.value === val
                    )
                    return option?.label || val
                })
                .join(', ')
        }

        // Handle single value
        const option = options?.find(
            opt => String(opt.value) === String(value) || opt.value === value
        )

        return option?.label || String(value) || '-'
    }

    const selectedOptionLabel = getSelectedLabel()

    return (
        <Field className='space-y-1'>
            <div>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                {description && (
                    <Description className='text-sm text-gray-500'>{description}</Description>
                )}
            </div>
            <div className='relative'>
                {readOnly ? (
                    <div
                        className={clsx(
                            'block w-full bg-transparent py-2',
                            'text-gray-900 min-h-[42px]', // Match the height of the select
                            className
                        )}
                    >
                        {selectedOptionLabel}
                    </div>
                ) : (
                    <>
                        <Select
                            className={clsx(
                                'w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm shadow-sm',
                                'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                                'disabled:bg-gray-100 disabled:text-gray-500 bg-white',
                                className
                            )}
                            defaultValue={defaultValue ?? ''}
                            disabled={readOnly}
                            {...props}
                        >
                            <option value='' disabled hidden>
                                {options?.length ? 'Select an option' : 'Loading options...'}
                            </option>
                            {options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                        <FaChevronDown className='pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-gray-400' />
                    </>
                )}
            </div>
            {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
        </Field>
    )
}

// rtf editor
export function FormRichText({
    label,
    description,
    className = '',
    error = '',
    required = false,
    readOnly = false,
    value = '',
    onChange = () => {},
}) {
    const { quill, quillRef } = useQuill({
        theme: 'snow',
        modules: {
            toolbar: [['bold', 'italic', 'underline', 'strike'], ['link'], ['clean']],
        },
    })

    // Set initial value + update editor if value changes
    useEffect(() => {
        if (quill) {
            if (quill.root.innerHTML !== value) {
                quill.root.innerHTML = value || ''
            }
        }
    }, [quill, value])

    // Listen for changes
    useEffect(() => {
        if (!quill) return

        const handler = () => {
            onChange(quill.root.innerHTML)
        }

        quill.on('text-change', handler)

        return () => {
            quill.off('text-change', handler)
        }
    }, [quill, onChange])

    return (
        <Field className='space-y-1'>
            <div>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                {description && (
                    <Description className='text-sm text-gray-500'>{description}</Description>
                )}
            </div>

            {readOnly ? (
                <div
                    className={clsx(
                        'block w-full bg-transparent py-2 border border-transparent text-sm text-gray-700',
                        className
                    )}
                    dangerouslySetInnerHTML={{ __html: value || '-' }}
                />
            ) : (
                <>
                    <div
                        className={clsx(
                            'rounded-lg border border-gray-300 shadow-sm bg-white',
                            'focus-within:border-yellow-500 focus-within:ring-2 focus-within:ring-yellow-200',
                            'resize-y overflow-auto',
                            className
                        )}
                    >
                        <div ref={quillRef} className='min-h-24' />
                    </div>

                    {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
                </>
            )}
        </Field>
    )
}

// Reusable Textarea Component
export function FormTextarea({
    label,
    description,
    className = '',
    error = '',
    required = false,
    readOnly = false,
    ...props
}) {
    return (
        <Field className='space-y-1'>
            <div>
                {label && (
                    <Label className='text-sm font-medium text-gray-700'>
                        {label} {required && !readOnly && <span className='text-red-500'>*</span>}
                    </Label>
                )}
                {description && (
                    <Description className='text-sm text-gray-500'>{description}</Description>
                )}
            </div>
            {readOnly ? (
                <span
                    className={clsx(
                        'block w-full bg-transparent py-2 border border-transparent',
                        className
                    )}
                >
                    {props.value || '-'}
                </span>
            ) : (
                <>
                    <Textarea
                        className={clsx(
                            'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm',
                            'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                            'disabled:bg-gray-100 disabled:text-gray-500',
                            className
                        )}
                        {...props}
                    />
                    {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
                </>
            )}
        </Field>
    )
}

// Reusable Fieldset Component
export function FormFieldset({ title, children, className = '' }) {
    return (
        <Fieldset className={clsx('space-y-4 rounded-lg bg-white p-6 shadow-sm', className)}>
            {title && <Legend className='text-lg font-semibold text-gray-900'>{title}</Legend>}
            {children}
        </Fieldset>
    )
}

// Example Usage
export function ShippingForm() {
    const countries = [
        { value: 'us', label: 'United States' },
        { value: 'ca', label: 'Canada' },
        { value: 'mx', label: 'Mexico' },
    ]

    return (
        <div className='max-w-md p-4 mx-auto'>
            <FormFieldset title='Shipping Details'>
                <FormInput label='Street Address' name='address' required />

                <FormSelect
                    label='Country'
                    description='We currently only ship to North America.'
                    name='country'
                    options={countries}
                    defaultValue='us'
                />

                <FormTextarea
                    label='Delivery Notes'
                    description='Special instructions for delivery'
                    name='notes'
                    rows={3}
                />
            </FormFieldset>
        </div>
    )
}
